import { ChevronDown } from 'lucide-react';
import type { Project } from '@timato/shared';
import { Menu, type MenuItem } from '@/components/ui';
import { cn } from '@/lib/cn';
import { ProjectDot } from './ProjectDot';

type Props = { projects: Project[]; value: Project | null; onChange: (p: Project | null) => void; allowNone?: boolean; size?: keyof typeof SIZE; className?: string };

// `chip` sits inside the goal input on the Timer; `field` stands beside an input and takes its height and corners.
const SIZE = {
  chip: 'h-7 pl-2 pr-[6px] rounded-chip text-[12px]',
  field: 'h-10 pl-3 pr-[10px] rounded-input text-[13px] shrink-0',
} as const;

/** Compact project selector (dot · name · chevron). Click opens the list; used in the goal combobox and the session form. */
export function ProjectChip({ projects, value, onChange, allowNone = false, size = 'chip', className }: Props) {
  // The same dot as the trigger, so the list can be scanned by colour the way the Projects page can.
  const items: MenuItem[] = projects.map((p) => ({ label: p.name, leading: <ProjectDot project={p} />, onSelect: () => onChange(p) }));
  if (allowNone) items.push({ label: 'No project', leading: <ProjectDot project={null} />, onSelect: () => onChange(null), separatorBefore: true });
  return (
    <Menu align="start" items={items} trigger={
      <button type="button" className={cn('inline-flex items-center gap-[6px] border border-border bg-card font-bold text-ink hover:bg-inset cursor-pointer', SIZE[size], className)} aria-label="Project">
        <ProjectDot project={value} /><span className="max-w-[140px] truncate">{value?.name ?? 'No project'}</span><ChevronDown size={12} className="text-ink-2" />
      </button>
    } />
  );
}
